import { colord } from 'colord'

// Enough stops that the straight hops between them read as the curve they
// sample rather than as segments.
const RAMP_STOPS = 9

/** Where each {@link hsbRamp} stop sits in a 0–1 crossing. */
export const RAMP_PROGRESS: number[] = Array.from(
  { length: RAMP_STOPS },
  (_, index) => index / (RAMP_STOPS - 1),
)

/**
 * The path from one fill colour to the next sampled through HSB, as stops a
 * plain colour interpolation can follow on the UI thread (Neil, ENG-6125:
 * white → green travels by brightness, not along the RGB line).
 *
 * An unsaturated colour has no hue of its own, so it borrows the other end's:
 * white otherwise leaves at hue 0 and the crossing flashes yellow on its way
 * to green. A colour `colord` cannot read crosses nowhere — every stop is the
 * colour being crossed to, as a wrong colour is worse than no motion.
 */
export function hsbRamp(from: string, to: string): string[] {
  const start = colord(from)
  const end = colord(to)
  if (!start.isValid() || !end.isValid()) {
    return RAMP_PROGRESS.map(() => to)
  }
  const a = start.toHsv()
  const b = end.toHsv()
  const fromHue = a.s === 0 ? b.h : a.h
  const toHue = b.s === 0 ? a.h : b.h
  // The short way round the wheel: 350° → 10° is 20° of travel, not 340°.
  const hueTravel = ((toHue - fromHue + 540) % 360) - 180
  return RAMP_PROGRESS.map((t, index) => {
    // The ends are the caller's own colours: HSB rounds, and the fill has to
    // come to rest on the theme's value, not near it.
    if (index === 0) {
      return from
    }
    if (index === RAMP_PROGRESS.length - 1) {
      return to
    }
    return colord({
      h: (fromHue + hueTravel * t + 360) % 360,
      s: a.s + (b.s - a.s) * t,
      v: a.v + (b.v - a.v) * t,
      a: a.a + (b.a - a.a) * t,
    }).toHex()
  })
}
